import { useEffect, useRef, useState } from "react";
import { FaVideo } from "@react-icons/all-files/fa/FaVideo";
import { FaImage } from "@react-icons/all-files/fa/FaImage";
import { FaExpand } from "@react-icons/all-files/fa/FaExpand";
import { FaSyncAlt } from "@react-icons/all-files/fa/FaSyncAlt";
import { formattedGlobalDate } from "../../utils/formattedDates";
import axios from "../../utils/axiosConfig";
import Hls from "hls.js";

const EMPTY_STREAM_URLS = Object.freeze([]);

const StreamThumbPreview = ({ url, title, className }) => {
  const thumbVideoRef = useRef(null);
  const thumbHlsRef = useRef(null);
  const isM3u8 = /\.m3u8(\?|#|$)/i.test(String(url || ""));

  useEffect(() => {
    const video = thumbVideoRef.current;
    if (!video || !url) return;

    if (thumbHlsRef.current) {
      thumbHlsRef.current.destroy();
      thumbHlsRef.current = null;
    }

    if (isM3u8) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        thumbHlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => { });
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = url;
        video.load();
        video.play().catch(() => { });
      }
    } else {
      video.src = url;
      video.load();
      video.play().catch(() => { });
    }

    return () => {
      if (thumbHlsRef.current) {
        thumbHlsRef.current.destroy();
        thumbHlsRef.current = null;
      }
    };
  }, [url, isM3u8]);

  return (
    <video
      ref={thumbVideoRef}
      title={title}
      className={className}
      muted
      playsInline
      autoPlay
      preload="metadata"
    />
  );
};

const EnVivoPanel = ({
  contrato,
  mensajes = [],
  initialStreamUrl = "",
  initialStreamVersion = 0,
  liveActive = false,
  deviceActiveStreamUrls = EMPTY_STREAM_URLS,
  hideStreamControls = false,
}) => {
  const pad = (value) => String(value).padStart(2, "0");

  const parseUtcInputToMs = (input) => {
    if (!input) return NaN;
    if (input instanceof Date) return input.getTime();

    const raw = String(input).trim();
    if (!raw) return NaN;

    const plainMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
    );
    if (plainMatch) {
      const [, y, mo, d, h, mi, s = "00"] = plainMatch;
      return Date.UTC(
        Number(y),
        Number(mo) - 1,
        Number(d),
        Number(h),
        Number(mi),
        Number(s)
      );
    }

    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? NaN : parsed;
  };

  const formatUtcDateTime = (ms) => {
    if (!Number.isFinite(ms)) return "";
    const date = new Date(ms);
    const year = date.getUTCFullYear();
    const month = pad(date.getUTCMonth() + 1);
    const day = pad(date.getUTCDate());
    const hour = pad(date.getUTCHours());
    const minute = pad(date.getUTCMinutes());
    const second = pad(date.getUTCSeconds());
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  };

  const normalizeUtcForPayload = (value) => {
    const ms = parseUtcInputToMs(value);
    if (!Number.isFinite(ms)) return value || null;
    return formatUtcDateTime(ms);
  };

  const parseStreamingUrls = (rawUrl) => {
    if (!rawUrl) return [];
    return String(rawUrl)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const mergeStreamingUrls = (...sources) => {
    const merged = [];
    const seen = new Set();
    sources.forEach((source) => {
      parseStreamingUrls(source).forEach((url) => {
        if (seen.has(url)) return;
        seen.add(url);
        merged.push(url);
      });
    });
    return merged;
  };


  const isJt800Url = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return false;
    if (raw.toLowerCase().includes("jt800")) return true;
    try {
      return decodeURIComponent(raw).toLowerCase().includes("jt800");
    } catch {
      return false;
    }
  };

  const [cameraStatus, setCameraStatus] = useState("idle");
  const [cameraError, setCameraError] = useState("");
  const [streamUrl, setStreamUrl] = useState("");
  const [streamOptions, setStreamOptions] = useState([]);
  const [selectedStreamUrl, setSelectedStreamUrl] = useState("");
  const [streamsRegistres, setStreamsRegistres] = useState([]);
  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const previewRef = useRef(null);
  const previewVideoRef = useRef(null);
  const hlsInstanceRef = useRef(null);
  const streamRef = useRef(null);
  const prevHasJt800StreamRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(true);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isStartingStream, setIsStartingStream] = useState(false);
  const [pendingStreamDuration, setPendingStreamDuration] = useState(null);
  const ensureMutedUrl = (url) => {
    if (!url) return url;
    const lower = String(url).toLowerCase();
    const hasMute = lower.includes("mute=");
    const hasMuted = lower.includes("muted=");
    const hasAutoplay = lower.includes("autoplay=");
    let next = url;
    if (!hasMute && !hasMuted) next += `${next.includes("?") ? "&" : "?"}mute=true`;
    if (!hasAutoplay) next += `${next.includes("?") ? "&" : "?"}autoplay=true`;
    return next;
  };
  const previewSrc = selectedFileUrl || streamUrl;
  const isM3u8Url = (url) => /\.m3u8(\?|#|$)/i.test(String(url || ""));
  const isMp4Url = (url) => /\.mp4(\?|#|$)/i.test(String(url || ""));
  const shouldUseVideoTag =
    !!previewSrc &&
    !(!!selectedFileUrl && isImageType(selectedFileType)) &&
    (isM3u8Url(previewSrc) || isMp4Url(previewSrc));
  const getStreamKind = (url) => {
    if (isM3u8Url(url)) return "m3u8";
    if (isMp4Url(url)) return "mp4";
    return "iframe";
  };
  const showLiveTag = !!streamUrl && !selectedFileUrl;
  const normalizedDeviceStreams = Array.isArray(deviceActiveStreamUrls)
    ? deviceActiveStreamUrls.map((u) => String(u || "").trim()).filter(Boolean)
    : [];
  // El bloqueo del botón debe depender solo de streams activos reales
  // del dispositivo, no de URLs históricas del contrato/carrusel.
  const hasJt800Stream = normalizedDeviceStreams.some((url) => isJt800Url(url));
  const isStreamLocked = hasJt800Stream || isStartingStream;

  const formatStreamingDurationLabel = (seconds) => {
    const value = Number(seconds);
    if (!Number.isFinite(value) || value <= 0) return "";
    if (value < 60) return `${value}s`;
    if (value % 60 === 0) return `${value / 60}m`;
    return `${value}s`;
  };

  const streamingStatusText = (() => {
    if (!isStreamLocked) return "";
    if (pendingStreamDuration) {
      return `Transmitiendo por ${formatStreamingDurationLabel(pendingStreamDuration)}...`;
    }
    return isStartingStream ? "Iniciando transmisión..." : "Transmitiendo en vivo...";
  })();

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setStreamUrl("");
  };

  const getRegisteredStreams = async () => {
    setIsRefreshing(true);
    const payload = {
      activo: contrato?.Active,
      deviceID: contrato?.FKLokDeviceID,
      fechainicio: normalizeUtcForPayload(contrato?.fecha),
      fechafin: normalizeUtcForPayload(contrato?.fechafin)
    }
    console.log("Obteniendo streams registrados con payload:", payload);
    try {
      const response = await axios.post(process.env.REACT_APP_SERVER + "/operaciones2/getstreamingvideos", JSON.stringify(payload), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('tkn')}`
        },
        withCredentials: true
      });
      console.log("Streams registrados:", response.data);
      console.log("Respuesta completa del servidor:", response.data?.data?.recordset);
      const records =
        (Array.isArray(response?.data?.data?.recordset) && response.data.data.recordset) ||
        (Array.isArray(response?.data?.data?.recordSet) && response.data.data.recordSet) ||
        (Array.isArray(response?.data?.data?.data) && response.data.data) ||
        (Array.isArray(response?.data?.data?.records) && response.data.records) ||
        (Array.isArray(response?.data?.data) && response.data.data) ||
        (Array.isArray(response?.data?.records) && response.data.records) ||
        (Array.isArray(response?.data) && response.data) ||
        [];
      console.log("Registros obtenidos:", records);
      setStreamsRegistres(records);
    } catch (error) {
      console.error("Error al obtener streams registrados:", error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  };

  const requestCamera = async (streaming) => {
    if (isStreamLocked) return;
    console.log("Solicitando cámara con streaming y duracion de:", streaming);
    console.log("iniciar streaming");
    setSelectedFileUrl("");
    setSelectedFileType("");
    setStreamUrl("");
    setCameraError("");
    setPendingStreamDuration(streaming);
    setIsStartingStream(true);
    let datos = {
      "deviceId": contrato?.FKLokDeviceID,
      // TODO: Revisar formato del comando para iniciar el streaming en el dispositivo, actualmente se usa un formato de prueba, validar con el equipo de backend el formato correcto y la URL de streaming a usar  
      // "commandHex": `(700160818000,1,001,CAMERA,5,1,${streaming},rtmp://20.57.36.169/LiveApp/${contrato?.FKLokDeviceID}_JT800)`,
      "commandHex": `(700160818000,1,001,CAMERA,5,1,${streaming},rtmp://streaming.cargotronics.com/LiveApp/${contrato?.FKLokDeviceID}_SERV02_JT800)`,
      "ttlSeconds": 600
    }
    console.log("Datos del comando:", datos);
    setCameraStatus("requesting");
    axios.post(process.env.REACT_APP_SERVER + "/operaciones2/sendgpscommand", JSON.stringify(datos), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem('tkn')}`
      },
      withCredentials: true
    })
      .then((response) => {
        console.log("Comando enviado:", response.data);
        // La URL final debe llegar por WebSocket en el evento LIVE_NOW.
        setCameraStatus("requesting");
      })
      .catch((error) => {
        console.error("Error al enviar comando:", error);
        setCameraError("Error al enviar comando de streaming.");
        setCameraStatus("error");
        setIsStartingStream(false);
        setPendingStreamDuration(null);
      });
  }

  const resolveFileUrl = (record) => {
    // console.log("Resolviendo URL para registro:", record);
    if (!record || typeof record !== "object") return "";
    return (
      record.FileName ||
      record.Url ||
      record.URL ||
      record.FileUrl ||
      record.FileURL ||
      record.fileUrl ||
      record.fileURL ||
      record.Path ||
      record.FilePath ||
      record.Ruta ||
      record.Location ||
      record.DownloadUrl ||
      record.StreamUrl ||
      record.streamUrl ||
      ""
    );
  };

  const getFileType = (record) => {
    if (!record) return "";
    return (
      record.FileType ||
      record.fileType ||
      record.Tipo ||
      record.Type ||
      record.Extension ||
      ""
    );
  };

  function isImageType(fileType) {
    if (!fileType) return false;
    const value = String(fileType).toLowerCase();
    return (
      value.includes("image") ||
      value.includes("img") ||
      value.includes("foto") ||
      value.includes("jpg") ||
      value.includes("jpeg") ||
      value.includes("png") ||
      value.includes("gif") ||
      value.includes("webp")
    );
  }
  const isSelectedImage = !!selectedFileUrl && isImageType(selectedFileType);

  const allowedStreamByContract = contrato?.allowstream;


  const areSameUrlList = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (String(a[i] || "") !== String(b[i] || "")) return false;
    }
    return true;
  };


  const toggleFullscreen = async () => {
    if (!previewRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await previewRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("No se pudo cambiar a pantalla completa:", error);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);


  useEffect(() => {
    return () => {
      stopCamera();
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };

  }, []);



  useEffect(() => {
    console.log("Contrato actualizado:", contrato);
    console.log("Stream activo en dispositivo:", allowedStreamByContract);
    getRegisteredStreams();
  }, [contrato]);

  useEffect(() => {
    // El carrusel de "Streams del dispositivo" usa exclusivamente active streams.
    const activeUrls = Array.isArray(deviceActiveStreamUrls)
      ? deviceActiveStreamUrls.map((u) => String(u || "").trim()).filter(Boolean)
      : [];
    const nextUrls = mergeStreamingUrls(activeUrls.join(","));

    if (nextUrls.length === 0) {
      if (streamOptions.length > 0) setStreamOptions([]);
      if (selectedStreamUrl) setSelectedStreamUrl("");
      if (!selectedFileUrl && streamUrl) setStreamUrl("");
      return;
    }
    const hasChanged = !areSameUrlList(nextUrls, streamOptions);
    if (!hasChanged) return;

    setStreamOptions(nextUrls);
    if (!nextUrls.includes(selectedStreamUrl)) {
      const primaryUrl = nextUrls[nextUrls.length - 1];
      setSelectedStreamUrl(primaryUrl);
      if (!selectedFileUrl) {
        setIsPreviewLoading(true);
        setStreamUrl(ensureMutedUrl(primaryUrl));
      }
    }
  }, [
    deviceActiveStreamUrls,
    selectedStreamUrl,
    selectedFileUrl,
    streamOptions,
  ]);

  useEffect(() => {
    if (!selectedStreamUrl) return;
    setSelectedFileUrl("");
    setSelectedFileType("");
    setIsPreviewLoading(true);
    setStreamUrl(ensureMutedUrl(selectedStreamUrl));
    setCameraStatus("ready");
  }, [selectedStreamUrl]);

  useEffect(() => {
    if (liveActive) {
      // Confirmación real de que el live quedó activo en backend/websocket.
      setIsStartingStream(false);
      return;
    }
    setIsStartingStream(false);
    setPendingStreamDuration(null);
  }, [liveActive]);

  useEffect(() => {
    if (!isStartingStream || liveActive) return;
    const timeoutId = setTimeout(() => {
      setIsStartingStream(false);
      setPendingStreamDuration(null);
      setCameraStatus("error");
      setCameraError("No se confirmó el inicio del en vivo. Intenta nuevamente.");
    }, 30000);
    return () => clearTimeout(timeoutId);
  }, [isStartingStream, liveActive]);

  useEffect(() => {
    // Si finaliza el stream JT800 que inició desde el botón, limpiamos
    // la duración pendiente para evitar que el botón quede en estado viejo.
    if (isStartingStream) return;
    if (hasJt800Stream) return;
    if (pendingStreamDuration !== null) {
      setPendingStreamDuration(null);
    }
  }, [hasJt800Stream, isStartingStream, pendingStreamDuration]);

  useEffect(() => {
    if (previewSrc) {
      setIsPreviewLoading(true);
    } else {
      setIsPreviewLoading(false);
    }
  }, [previewSrc]);

  useEffect(() => {
    // Refresco liviano mientras hay stream activo para que la lista se mantenga al día.
    if (!hasJt800Stream) return;
    const intervalId = setInterval(() => {
      getRegisteredStreams().catch(() => { });
    }, 15000);
    return () => clearInterval(intervalId);
  }, [hasJt800Stream, contrato?.FKLokDeviceID, contrato?.ContractID]);

  useEffect(() => {
    // Cuando termina el stream (activo -> inactivo), refrescamos la lista automáticamente.
    const wasActive = prevHasJt800StreamRef.current;
    prevHasJt800StreamRef.current = hasJt800Stream;
    if (!wasActive || hasJt800Stream) return;

    const timeoutId = setTimeout(() => {
      getRegisteredStreams().catch(() => { });
    }, 2500);

    return () => clearTimeout(timeoutId);
  }, [hasJt800Stream, contrato?.FKLokDeviceID, contrato?.ContractID]);

  useEffect(() => {
    const video = previewVideoRef.current;
    if (!video || !shouldUseVideoTag || !previewSrc) return;

    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }

    if (isM3u8Url(previewSrc)) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        hlsInstanceRef.current = hls;
        hls.loadSource(previewSrc);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => { });
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = previewSrc;
        video.load();
        video.play().catch(() => { });
      } else {
        setCameraError("Este navegador no soporta reproducción HLS.");
      }
      return;
    }

    if (isMp4Url(previewSrc)) {
      video.src = previewSrc;
      video.load();
      video.play().catch(() => { });
    }
  }, [previewSrc, shouldUseVideoTag]);

  useEffect(() => {
    if (!isPreviewLoading) return;
    const timeoutId = setTimeout(() => {
      setIsPreviewLoading(false);
    }, 12000);
    return () => clearTimeout(timeoutId);
  }, [isPreviewLoading]);

  const getRecordDatetime = (record) => (
    record?.UtcDatetime ||
    record?.utcDatetime ||
    record?.UTCDateTime ||
    record?.Fecha ||
    record?.Date ||
    ""
  );

  const orderedStreams = [...streamsRegistres].sort((a, b) => {
    const aTime = parseUtcInputToMs(getRecordDatetime(a));
    const bTime = parseUtcInputToMs(getRecordDatetime(b));
    const aInvalid = Number.isNaN(aTime);
    const bInvalid = Number.isNaN(bTime);
    if (aInvalid && bInvalid) return 0;
    if (aInvalid) return 1;
    if (bInvalid) return -1;
    return bTime - aTime;
  });



  const [streamingDuration, setStreamingDuration] = useState(30);

  return (
    <div className="en-vivo-panel">
      <div className="en-vivo-layout">
        <div className="en-vivo-left">
          <div className="en-vivo-card">
            <div className="en-vivo-title">
              {showLiveTag &&
                <>
                  <span className="en-vivo-live-dot" aria-hidden="true"></span>
                  <span>En vivo</span>
                </>
              }
              {!showLiveTag && previewSrc && <span>Archivo</span>}
            </div>
            {/* <p className="en-vivo-text">
              Para continuar, autoriza el acceso a la cámara. La transmisión se mostrará aquí.
            </p>
            {contrato?.ContractID && (
              <div className="en-vivo-contrato">Contrato: {contrato.ContractID}</div>
            )} */}
            <div className={`en-vivo-preview ${isFullscreen ? "is-fullscreen" : ""}`} ref={previewRef}>
              {previewSrc ? (
                isSelectedImage ? (
                  <img
                    key={`img-${previewSrc}`}
                    src={previewSrc}
                    alt="Archivo seleccionado"
                    className="en-vivo-media"
                    onLoad={() => setIsPreviewLoading(false)}
                    onError={() => setIsPreviewLoading(false)}
                  />
                ) : shouldUseVideoTag ? (
                  <video
                    key={`video-${previewSrc}`}
                    ref={previewVideoRef}
                    className="en-vivo-media"
                    controls
                    autoPlay
                    muted
                    playsInline
                    onLoadedData={() => setIsPreviewLoading(false)}
                    onCanPlay={() => setIsPreviewLoading(false)}
                    onError={() => setIsPreviewLoading(false)}
                  />
                ) : (
                  <iframe
                    key={`iframe-${previewSrc}`}
                    title="Streaming en vivo"
                    src={ensureMutedUrl(previewSrc)}
                    className="en-vivo-iframe"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    onLoad={() => setIsPreviewLoading(false)}
                    onError={() => setIsPreviewLoading(false)}
                  />
                )
              ) : null}
              {isSelectedImage && (
                <div className="en-vivo-overlay-actions">
                  <button
                    type="button"
                    className="en-vivo-fullscreen-btn"
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                    title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                  >
                    <FaExpand aria-hidden="true" />
                  </button>
                </div>
              )}
              {isPreviewLoading && (
                <div className="en-vivo-loader" aria-live="polite">
                  <span className="en-vivo-spinner" aria-hidden="true"></span>
                  <span>{isSelectedImage ? "Cargando archivo..." : "Cargando streaming..."}</span>
                </div>
              )}
              {!previewSrc && cameraStatus !== "ready" && (
                <div className="en-vivo-placeholder">
                  {cameraStatus === "requesting" && "Solicitando permiso…"}
                  {cameraStatus === "denied" && "Permiso denegado"}
                  {cameraStatus === "error" && "Cámara no disponible"}
                  {cameraStatus === "idle" && "Vista previa de cámara"}
                </div>
              )}
            </div>
            {cameraError && <div className="en-vivo-error">{cameraError}</div>}
            {streamOptions.length > 1 && (
              <div className="en-vivo-stream-carousel-wrap">
                <div className="en-vivo-stream-carousel-title">Streams del dispositivo</div>
                <div className="en-vivo-stream-carousel" role="list" aria-label="Streams disponibles">
                  {streamOptions.map((url, index) => {
                    const isPrimary = index === streamOptions.length - 1;
                    const isActive = selectedStreamUrl === url;
                    return (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        className={`en-vivo-stream-thumb ${isActive ? "is-active" : ""}`}
                        onClick={() => setSelectedStreamUrl(url)}
                        disabled={isPreviewLoading}
                        title={isPrimary ? `Streaming ${index + 1} (Principal)` : `Streaming ${index + 1}`}
                        aria-pressed={isActive}
                        role="listitem"
                      >
                        {getStreamKind(url) === "iframe" ? (
                          <iframe
                            title={`stream-thumb-${index + 1}`}
                            src={ensureMutedUrl(url)}
                            className="en-vivo-stream-thumb-frame"
                            loading="lazy"
                            allow="autoplay; fullscreen"
                          />
                        ) : (
                          <StreamThumbPreview
                            title={`stream-thumb-${index + 1}`}
                            url={url}
                            className="en-vivo-stream-thumb-frame"
                          />
                        )}
                        <span className="en-vivo-stream-thumb-label">
                          {isPrimary ? `Streaming ${index + 1} (Principal)` : `Streaming ${index + 1}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!hideStreamControls && allowedStreamByContract && (
              <div className="en-vivo-actions">
                <div className="en-vivo-stream-control">
                  <button
                    type="button"
                    className="en-vivo-stream-btn"
                    onClick={() => requestCamera(streamingDuration)}
                    disabled={isStreamLocked}
                  >
                    {isStreamLocked ? streamingStatusText : "Iniciar streaming"}
                  </button>
                  <select
                    className="en-vivo-duration-select"
                    value={streamingDuration}
                    onChange={(e) => setStreamingDuration(Number(e.target.value))}
                    aria-label="Duración del streaming en segundos"
                    disabled={isStreamLocked}
                  >
                    <option value={30}>30s</option>
                    <option value={60}>1m</option>
                    <option value={120}>2m</option>
                    <option value={180}>3m</option>
                    <option value={240}>4m</option>
                    <option value={300}>5m</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="en-vivo-right">
          <div className={`en-vivo-tracking ${trackingOpen ? "" : "is-collapsed"}`}>
            <div className="en-vivo-tracking-header">
              <button
                type="button"
                className="en-vivo-tracking-toggle"
                onClick={() => setTrackingOpen((prev) => !prev)}
                disabled={isStreamLocked}
              >
                {trackingOpen ? "Ocultar" : "Mostrar"}
              </button>
            </div>

            <div className="en-vivo-info-grid">
              <div>
                <span>Dispositivo:</span> {contrato?.FKLokDeviceID || "—"}
              </div>
              <div>
                <span>Contrato:</span> {contrato?.ContractID || "—"}
              </div>
              <div>
                <span>Cliente:</span> {contrato?.NombreEmpresa || "—"}
              </div>
              <div>
                <span>Ruta:</span> {contrato?.DescripcionRuta || "—"}
              </div>
            </div>

            <div className="en-vivo-tab-title">
              Lista de archivos
              <span className="en-vivo-counter">Total: {streamsRegistres.length}</span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm en-vivo-refresh-btn"
                onClick={getRegisteredStreams}
                disabled={isRefreshing || isStreamLocked}
              >
                {isRefreshing ? (
                  <span className="en-vivo-refresh-spinner" aria-hidden="true"></span>
                ) : (
                  <FaSyncAlt aria-hidden="true" className="en-vivo-refresh-icon" />
                )}
                <span>{isRefreshing ? "Actualizando..." : "Actualizar"}</span>
              </button>
            </div>
            <div className="en-vivo-table">
              <table>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {streamsRegistres.length === 0 && (
                    <tr>
                      <td colSpan={3} className="en-vivo-empty">Sin archivos registrados</td>
                    </tr>
                  )}
                  {orderedStreams.map((record, index) => {
                    const fileType = getFileType(record);
                    const fileUrl = resolveFileUrl(record);
                    const isImage = isImageType(fileType);
                    const iconTitle = isImage ? "Ver imagen" : "Ver video";
                    const datetime = getRecordDatetime(record);
                    return (
                      <tr key={record?.Id || record?.ID || record?.id || index}>
                        <td>{fileType || "—"}</td>
                        <td>{datetime ? formattedGlobalDate(datetime) : "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="en-vivo-file-btn"
                            title={iconTitle}
                            disabled={!fileUrl || isPreviewLoading || isStreamLocked}
                            onClick={() => {
                              if (!fileUrl) return;
                              setIsPreviewLoading(true);
                              setSelectedFileUrl(fileUrl);
                              setSelectedFileType(fileType || "");
                            }}
                          >
                            {isImage ? <FaImage aria-hidden="true" /> : <FaVideo aria-hidden="true" />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );


};

export default EnVivoPanel;












